import _ from "lodash";
import React from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  DEFAULT_CONCURRENCY,
  mapWithConcurrency,
  withRetry,
} from "asyncPool";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  CircularProgress,
  Button,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { green, red } from "@mui/material/colors";

// "Make Certs" calls the server-side `generateCert` Cloud Function (see
// functions/), which renders and stores the Certificate of Conformance with
// the Quality Director signature and fonts kept server-side — none of that
// ships in this bundle anymore. The function enforces certifiability itself;
// the check below only decides which items to submit, so non-certifiable
// items are skipped silently, as before. A progress dialog shows per-item
// status as each certificate is generated.
//
// Requests are submitted a few at a time and transient failures are retried
// once: every item used to be fired at once with no retry, so a batch hitting
// a cold function could report sporadic INTERNAL/500s that succeeded on a
// manual re-run.
const App = () => {
  const { enqueueSnackbar } = useSnackbar();
  const items = useSelector((state) => state.jobItems);
  const reports = useSelector((state) => state.jobReports);
  // null = dialog closed; otherwise an array of { id, asset, status, message }.
  const [progress, setProgress] = React.useState(null);

  const isCertifiable = (item, itemReports) => {
    const findTrace = (part) => _.map(part, (c) => c.materialId || "untraced");
    const itemTrace = [
      ...findTrace(item.connections),
      ...findTrace(item.bodies),
    ];
    const needsMet = _.map(item.needs, (need) => {
      if (need === "MTRs") return !_.includes(itemTrace, "untraced");
      return Boolean(_.find(itemReports, (r) => r.type && r.type.abbr === need));
    });
    return !_.includes(needsMet, false);
  };

  const makeCerts = async () => {
    // Ignore re-clicks while a batch is still running.
    if (progress && progress.some((r) => r.status === "pending")) return;

    const certifiable = _.pickBy(items, (item, itemId) => {
      const itemReports = _.pickBy(
        reports,
        (jr) => jr.items && jr.items[itemId]
      );
      return isCertifiable(item, itemReports);
    });
    const ids = Object.keys(certifiable);
    if (ids.length === 0) {
      enqueueSnackbar("No items are ready to certify.", { variant: "warning" });
      return;
    }

    setProgress(
      ids.map((id) => ({
        id,
        asset: certifiable[id].asset || id,
        status: "pending",
      }))
    );

    const generateCert = httpsCallable(
      getFunctions(),
      "assetConstruction-generateCert"
    );
    const patch = (itemId, update) =>
      setProgress((prev) =>
        prev
          ? prev.map((r) => (r.id === itemId ? { ...r, ...update } : r))
          : prev
      );

    await mapWithConcurrency(ids, DEFAULT_CONCURRENCY, async (itemId) => {
      try {
        await withRetry(() => generateCert({ jobItemId: itemId }), {
          onRetry: () => patch(itemId, { retrying: true }),
        });
        patch(itemId, { status: "success", retrying: false });
      } catch (e) {
        patch(itemId, {
          status: "error",
          retrying: false,
          message: (e && e.message) || String(e),
        });
      }
    });
  };

  const done = progress
    ? progress.filter((r) => r.status !== "pending").length
    : 0;
  const total = progress ? progress.length : 0;
  const complete = Boolean(progress) && done === total;
  const failed = progress ? progress.filter((r) => r.status === "error").length : 0;

  return (
    <>
      <div onClick={makeCerts}>Make Certs</div>
      <Dialog
        open={Boolean(progress)}
        onClose={complete ? () => setProgress(null) : undefined}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {complete ? "Certificates complete" : "Generating certificates…"}
        </DialogTitle>
        <DialogContent>
          <LinearProgress
            variant="determinate"
            value={total ? (done / total) * 100 : 0}
            sx={{ mb: 1, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {done} of {total} done{failed ? ` · ${failed} failed` : ""}
          </Typography>
          <List dense>
            {_.map(progress, (r) => (
              <ListItem key={r.id} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {r.status === "pending" ? (
                    <CircularProgress size={18} />
                  ) : r.status === "success" ? (
                    <CheckCircleIcon sx={{ color: green[600] }} />
                  ) : (
                    <ErrorIcon sx={{ color: red[600] }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={r.asset}
                  secondary={
                    r.status === "error"
                      ? r.message
                      : r.retrying
                      ? "retrying…"
                      : null
                  }
                  secondaryTypographyProps={{
                    color: r.status === "error" ? red[600] : "text.secondary",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgress(null)} disabled={!complete}>
            {complete ? "Close" : "Working…"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default App;
